import { jsx as _jsx } from "react/jsx-runtime";
import React, { Fragment, useCallback, useMemo, useRef, useState, } from "react";
import { v4 } from "uuid";
import ObjectService from "../../Services/Shared/Object.service";
import useUniqueId from "./useUniqueId";
/**
 * A hook to help with drag and drop reordering of fields in a form.
 *
 * @returns {UseFormDndReturn} a form ref and a function to get the form state
 */
const useFormDnd = () => {
    var _a;
    const uniqueId = useUniqueId()[0];
    const [fieldOrder, setFieldOrder] = useState([]);
    const [fieldName, setFieldName] = useState();
    const contextRef = useRef();
    const fieldDefaultValues = fieldName
        ? (_a = contextRef.current) === null || _a === void 0 ? void 0 : _a.watch(fieldName)
        : null;
    const makeNameFactory = useCallback((id) => (name) => `${uniqueId}.${id}` + (name ? `.${name}` : ""), [uniqueId]);
    const init = useCallback((fn, context) => {
        contextRef.current = context;
        // Disallow the name changing
        setFieldName((f) => f !== null && f !== void 0 ? f : fn);
        // If there aren't any values for field name, populate them because they
        // could be coming from default values
        if (!fieldOrder.length && fieldDefaultValues) {
            // set the fieldOrder
            const defaultFieldOrder = fieldDefaultValues.map(() => v4());
            setFieldOrder(defaultFieldOrder);
            // set the values in the form.
            context.reset(Object.assign(Object.assign({}, context.getValues({ nest: true })), { [uniqueId]: defaultFieldOrder.reduce((sum, key, index) => (Object.assign(Object.assign({}, sum), { [key]: fieldDefaultValues[index] })), {}) }));
        }
    }, [fieldDefaultValues, fieldOrder.length, uniqueId]);
    const move = useCallback((from, to) => {
        const items = Array.from(fieldOrder);
        const [reorderedItem] = items.splice(from, 1);
        items.splice(to, 0, reorderedItem);
        setFieldOrder(items);
    }, [fieldOrder]);
    const renderField = useCallback((render) => {
        // Render each one of the fields in order.
        return fieldOrder.map((key, index) => {
            return (
            // Adding a key here is super important so that on remount the component doesn't unmount and remount, which would
            // clear the form.
            _jsx(Fragment, { children: render({
                    makeName: makeNameFactory(key),
                    remove: () => {
                        setFieldOrder((s) => s.filter((elm) => elm !== key));
                    },
                    index,
                    key,
                }) }, key));
        });
    }, [fieldOrder, makeNameFactory]);
    const watch = useCallback(() => {
        var _a, _b;
        const val = (_b = (_a = contextRef.current) === null || _a === void 0 ? void 0 : _a.watch(uniqueId)) !== null && _b !== void 0 ? _b : [];
        return fieldOrder.map((key) => val[key]).filter((f) => f);
    }, [fieldOrder, uniqueId]);
    return useMemo(() => ({
        withState: (handleSubmit) => (formState) => {
            if (!fieldName) {
                console.warn("No field name provided to useFormDnd, just returning formState.");
                return formState;
            }
            // Copy the form state so we don't mutate it
            const copy = JSON.parse(JSON.stringify(formState));
            if (!(uniqueId in copy)) {
                console.warn("could not find unique id " +
                    uniqueId +
                    " in form state.  Just returning form state");
                return formState;
            }
            ObjectService.setPropertyAtPath(copy, fieldName, fieldOrder.map((key) => copy[uniqueId][key]));
            delete copy[uniqueId];
            return handleSubmit(copy);
        },
        formRef: {
            init,
            append: (newItem) => {
                var _a, _b;
                const id = v4();
                setFieldOrder((fo) => fo.concat(id));
                // Add values to form
                const currentValue = (_a = contextRef.current) === null || _a === void 0 ? void 0 : _a.getValues({ nest: true });
                if (currentValue) {
                    (_b = contextRef.current) === null || _b === void 0 ? void 0 : _b.reset(Object.assign(Object.assign({}, currentValue), { [uniqueId]: Object.assign(Object.assign({}, currentValue[uniqueId]), { [id]: newItem }) }));
                    // This is a little hacky, but there seem to be some kind of bug where
                    // calling reset to have a field include a new value, and then rendering that new value in the form will
                    // cause watches on the value to not fire until another render occurs.
                    // (We want to use reset because it allows us to set values before the fields that correspond to those values
                    // are rendered in the form).
                    // So, we set the fields again using setValue, which causes the watches to begin firing.
                    // We pick 100 ms because it should be plenty of time for the form to render, but short enough that it won't be
                    // very noticeable.
                    // The risk of 100 being not enough time is fairly small -- anything the user types in isn't lost, it will still
                    // make its way into handleSubmit.
                    setTimeout(() => {
                        var _a, _b;
                        const cv = (_a = contextRef.current) === null || _a === void 0 ? void 0 : _a.getValues({ nest: true });
                        if (cv) {
                            (_b = contextRef.current) === null || _b === void 0 ? void 0 : _b.setValue(uniqueId, Object.assign(Object.assign({}, cv[uniqueId]), { [id]: newItem }));
                        }
                    }, 100);
                }
            },
            watch,
            renderField,
            move,
        },
    }), [fieldName, fieldOrder, init, move, renderField, uniqueId, watch]);
};
export default useFormDnd;
